import { faMemo } from '@fortawesome/pro-regular-svg-icons';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Rarity } from '__generated__/globalTypes';
import { IconButton } from 'atoms/buttons/IconButton';
import { Shards } from 'atoms/icons/Shards';
import { Centered } from 'atoms/layout/Centered';
import { Horizontal, Vertical } from 'atoms/layout/flex';
import { BrandL, BrandS } from 'atoms/typography';
import { MY_SORARE_TRANSACTIONS_ESSENCE } from 'constants/__generated__/routes';
import { useCurrentUserContext } from 'contexts/currentUser';
import { usePinnedShardRarity } from 'hooks/config/usePinnedShardRarity';
import { useLifecycle } from 'hooks/useLifecycle';
import { type SupportedCardSet } from 'lib/cardSets';
import { getRarityColor } from 'lib/colors';
import { laptopAndAbove } from 'style/mediaQuery';
import type { Color } from 'style/types';

import { ShardRarityItem } from './ShardRarityItem';
import { useFilteredShardsChests } from './useFilteredShardsChests';

const MIX_SHARD_COLOR: Color = 'var(--c-green-400)';

const getRarityShardColor = (
  rarity: Nullable<Rarity>,
  cardSet: Nullable<SupportedCardSet>
) => {
  if (rarity) {
    return getRarityColor(rarity, { cardSet });
  }
  return MIX_SHARD_COLOR;
};

const SelectedCurrencyWrapper = styled(Vertical).attrs({
  gap: 2,
})`
  position: relative;
  padding: var(--double-unit) var(--unit) var(--unit) var(--unit);
  border: 1px solid var(--c-nd-150);
  border-radius: var(--intermediate-unit);
`;

const CenteredTop = styled(Centered)`
  align-items: start;
`;

const RaritiesWrapper = styled(Horizontal)`
  align-items: stretch;
  overflow-x: auto;
  overflow-y: hidden;
  @media ${laptopAndAbove} {
    flex-direction: column;
    gap: var(--half-unit);
  }
`;

type Props = {
  onClick?: () => void;
};

export const ShardDropdownDetails = ({ onClick }: Props) => {
  const { currentUser } = useCurrentUserContext();
  const { pinnedShardRarity, pinnedShardCardSet } = usePinnedShardRarity();
  const { lifecycle } = useLifecycle();
  const filteredShardsChests = useFilteredShardsChests();

  const { cardShardsChests } = currentUser || {};

  const shardsAmount = pinnedShardRarity
    ? cardShardsChests?.find(({ rarity }) => rarity === pinnedShardRarity)
        ?.cardShardsCount || 0
    : cardShardsChests
        ?.filter(
          ({ rarity }) =>
            !lifecycle?.hideCommonFeatures || rarity !== Rarity.common
        )
        .reduce((sum, { cardShardsCount }) => sum + cardShardsCount, 0) || 0;

  return (
    <SelectedCurrencyWrapper>
      <CenteredTop
        right={
          <IconButton
            size="small"
            color="quaternary"
            to={MY_SORARE_TRANSACTIONS_ESSENCE}
            icon={faMemo}
          />
        }
      >
        <Vertical gap={0.5} center>
          <Shards
            size={2}
            color={getRarityShardColor(pinnedShardRarity, pinnedShardCardSet)}
          />
          <BrandS
            color={getRarityShardColor(pinnedShardRarity, pinnedShardCardSet)}
          >
            <FormattedMessage
              id="useUserCurrencies.essence"
              defaultMessage="Essence"
            />
          </BrandS>
          <BrandL>
            <FormattedNumber value={shardsAmount || 0} />
          </BrandL>
        </Vertical>
      </CenteredTop>
      <RaritiesWrapper>
        {filteredShardsChests?.map(({ cardShardsCount, rarity, cardSet }) => {
          return (
            <ShardRarityItem
              key={`${rarity}${cardSet ? `:${cardSet.slug}` : ''}`}
              rarity={rarity}
              cardShardsCount={cardShardsCount}
              cardSet={cardSet}
              onClick={onClick}
            />
          );
        })}
      </RaritiesWrapper>
    </SelectedCurrencyWrapper>
  );
};
